/**
 * @fileoverview Shared address-validation hook for the contact package: Mapbox
 *   as-you-type candidate suggestions + USPS standardization, offline-aware.
 * @module ui-admin/utils
 *
 * Responsibilities:
 * - Provide useAddressValidation() — a host-neutral React hook that wraps the
 *   core-domain breakouts: suggestAddressCandidates (Mapbox) and
 *   standardizeAddress (POST /api/validate/address).
 * - Track suggestion + standardization status for the StructuredAddressField UI.
 *
 * Dependencies:
 * - react (peer)
 * - @firesquirrel/core-domain (suggestAddressCandidates, standardizeAddress,
 *   evaluatePostalAddressFormat, AddressCandidate, PostalAddress)
 *
 * Architecture:
 * - Same online-breakout precedent as useEmailValidation: the USPS call goes to
 *   the relative `/api/validate/address` so each host app's Vite dev proxy /
 *   production reverse proxy routes it to its own API. The Mapbox token is a
 *   public client token injected by the host (never hardcoded).
 * - Offline-aware: when navigator.onLine is false, suggest() returns [] and
 *   standardize() resolves to the local format-only result without a request.
 *
 * Notes:
 * - Components stay presentation-only and delegate all network work to this hook.
 */

import { useState, useCallback, useRef } from 'react';
import {
  suggestAddressCandidates,
  standardizeAddress,
  evaluatePostalAddressFormat,
  type AddressCandidate,
  type AddressProximity,
  type PostalAddress,
} from '@firesquirrel/core-domain';

/** Relative API base so the host proxy routes /api/validate/address correctly. */
const ADDRESS_API_BASE = '/api';

export type SuggestStatus = 'idle' | 'loading' | 'done' | 'offline';
export type StandardizeStatus = 'idle' | 'checking' | 'done' | 'offline' | 'error';

export interface AddressValidationState {
  suggestions: AddressCandidate[];
  suggestStatus: SuggestStatus;
  standardizeStatus: StandardizeStatus;
}

/**
 * @hook useAddressValidation
 *
 * Purpose:
 * - Provide Mapbox typeahead suggestions and USPS standardization for a
 *   structured postal address, with graceful offline degradation.
 *
 * Returns:
 * - suggestions, suggestStatus, standardizeStatus
 * - suggest(query, mapboxToken, proximity?): fetch ranked candidates
 *   (debounce in the caller); proximity biases results toward a point (e.g.
 *   the tenant's department coverage centroid) without excluding anything.
 * - clearSuggestions(): reset the candidate list
 * - standardize(address): resolve the USPS-canonical (or local) PostalAddress
 * - reset(): clear all state and abort in-flight suggestion work
 *
 * Side Effects:
 * - suggest() calls Mapbox directly (public token); standardize() posts to
 *   /api/validate/address through the host app's reverse proxy.
 *
 * Offline Behavior:
 * - suggest() → [] with status 'offline'; standardize() → local format result
 *   with status 'offline'. Neither blocks the user from saving.
 */
export function useAddressValidation() {
  const [state, setState] = useState<AddressValidationState>({
    suggestions: [],
    suggestStatus: 'idle',
    standardizeStatus: 'idle',
  });
  const suggestSeq = useRef(0);

  const suggest = useCallback(async (query: string, mapboxToken?: string, proximity?: AddressProximity): Promise<void> => {
    if (!mapboxToken || !navigator.onLine) {
      setState((prev) => ({ ...prev, suggestions: [], suggestStatus: navigator.onLine ? 'idle' : 'offline' }));
      return;
    }
    const seq = ++suggestSeq.current;
    setState((prev) => ({ ...prev, suggestStatus: 'loading' }));
    const candidates = await suggestAddressCandidates(query, { mapboxToken, proximity });
    // Ignore stale responses (a newer keystroke superseded this request).
    if (seq !== suggestSeq.current) return;
    setState((prev) => ({ ...prev, suggestions: candidates, suggestStatus: 'done' }));
  }, []);

  const clearSuggestions = useCallback(() => {
    suggestSeq.current++;
    setState((prev) => ({ ...prev, suggestions: [], suggestStatus: 'idle' }));
  }, []);

  const standardize = useCallback(async (address: Partial<PostalAddress>): Promise<PostalAddress> => {
    const local = evaluatePostalAddressFormat(address) ?? evaluatePostalAddressFormat({ line1: '' })!;
    if (!navigator.onLine) {
      setState((prev) => ({ ...prev, standardizeStatus: 'offline' }));
      return local;
    }
    setState((prev) => ({ ...prev, standardizeStatus: 'checking' }));
    try {
      const result = await standardizeAddress(address, { apiBaseUrl: ADDRESS_API_BASE });
      setState((prev) => ({ ...prev, standardizeStatus: 'done' }));
      return result;
    } catch {
      setState((prev) => ({ ...prev, standardizeStatus: 'error' }));
      return local;
    }
  }, []);

  const reset = useCallback(() => {
    suggestSeq.current++;
    setState({ suggestions: [], suggestStatus: 'idle', standardizeStatus: 'idle' });
  }, []);

  return { ...state, suggest, clearSuggestions, standardize, reset };
}
